import React, {FC, useEffect} from "react";
import Box from "@material-ui/core/Box";
import ShowInfoIcon from "@material-ui/icons/MenuBook";
import {generatePath, matchPath, useLocation} from "react-router-dom";
import {useTranslate} from "@tolgee/react";
import {useCurrentShow, useCurrentShowId} from "hooks";
import {
  DashboardIcon,
  ShoppingIcon,
  TaskReviewIcon,
} from "view/images/navigationIcons";
import {useDispatch, useSelector} from "react-redux";
import {getBadges, setShowCartCount} from "store/badges/slice";
import OrdersIcon from "@material-ui/icons/Receipt";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import Divider from "@material-ui/core/Divider";
import HowToRegIcon from "@material-ui/icons/HowToReg";
import {
  SHOW_EXHIBITOR_CART_PATH,
  SHOW_EXHIBITOR_DASHBOARD_PATH,
  SHOW_EXHIBITOR_ORDERS_PATH,
  SHOW_EXHIBITOR_SHOPPING_PATH,
  SHOW_EXHIBITOR_TASKS_PATH,
  SHOW_EXHIBITOR_USERS_PATH,
  SHOW_INFO_EXHIBITOR_PATH,
  SHOW_ORGANISER_SHOPPING_PATH,
} from "view/routes";
import stripeService from "api/stripe";
import {getItem} from "tools/localStorage";
import useLocalizedLocalMap from "hooks/useLocalizedLocalMap";
import {LocalStorageProduct} from "ts/api/product";
import {ThemeProvider} from "@material-ui/core";
import {useGenerateThemeFromBranding} from "view/apps/Exhibitor/helpers";
import useCurrentExhibitorPairId from "hooks/useCurrentExhibitorPairId";
import useCurrentExhibitorPair from "hooks/useCurrentExhibitorPair";
import useBranding from "store/branding/branding.store";
import useViewMode, {ViewMode} from "store/viewMode/viewMode.store";
import {IExhibitorPair, Roles} from "@evennt-tech/api-v2-core";
import {PermissionPackage} from "@evennt-tech/api-v2-core/dist/organiser";
import {getProductsTotalQuantity} from "../ShoppingPage/helpers";
import NavItem, {NavList} from "./Nav";

const ShowNavigation: FC = () => {
  const showId = useCurrentShowId();
  const [show] = useCurrentShow();
  const location = useLocation();
  const {t} = useTranslate();
  const badges = useSelector(getBadges);
  let exhibitorPairId = useCurrentExhibitorPairId();

  let [exhibitorPair] = useCurrentExhibitorPair({
    exhibitorPairId,
    showId,
  });
  const {mocked, isViewOnly, mode} = useViewMode();

  if (mode === ViewMode.EXHIBITOR && !exhibitorPair && mocked) {
    exhibitorPair = mocked.exhibitor as IExhibitorPair;
    exhibitorPairId = mocked.exhibitor?.id as string;
  }
  const dispatch = useDispatch();

  useEffect(() => {
    const productsItems = getItem<LocalStorageProduct[]>("products", []);
    if (productsItems?.length) {
      const productsTotalQuantity = getProductsTotalQuantity(productsItems);
      dispatch(setShowCartCount(productsTotalQuantity));
    } else {
      dispatch(setShowCartCount(0));
    }
  }, [dispatch]);

  const localMap = useLocalizedLocalMap(show, exhibitorPair?.stand?.builder);

  useEffect(() => {
    if (mode === ViewMode.EXHIBITOR || !showId || !exhibitorPairId) {
      return;
    }

    stripeService
      .deleteAbandonedCardOrders({showId, exhibitorPairId})
      .catch((error) => {
        console.error("Error deleting abandoned card orders: ", error);
      });
  }, [location.pathname, mode, showId, exhibitorPairId]);

  if (!show || !exhibitorPairId || !exhibitorPair) {
    return null;
  }
  const isTaskdIncluded = show.permissionPackages?.includes(
    PermissionPackage.TASKD
  );
  const isVendrIncluded =
    show.permissionPackages?.includes(PermissionPackage.VENDR) &&
    !!show.isShopEnabled;

  const createLinkPath = (path: string, args: Record<string, string>) => {
    if (isViewOnly) {
      return "#";
    }
    return generatePath(path, {...args});
  };

  const shopPathes = [
    createLinkPath(SHOW_EXHIBITOR_SHOPPING_PATH, {showId, exhibitorPairId}),
    createLinkPath(SHOW_ORGANISER_SHOPPING_PATH, {
      showId,
    }),
  ];
  const shopPathSelected = shopPathes.some((path) =>
    matchPath(location.pathname, path)
  );

  return (
    <Box>
      {isTaskdIncluded && (
        <NavList>
          <NavItem
            to={createLinkPath(SHOW_EXHIBITOR_DASHBOARD_PATH, {
              showId,
              exhibitorPairId,
            })}
            selected={mode === ViewMode.EXHIBITOR}
            icon={DashboardIcon}
            name={t("exhibitor.dashboard.title")}
            userRole={Roles.EXHIBITOR}
          />
          <NavItem
            to={createLinkPath(SHOW_EXHIBITOR_TASKS_PATH, {
              showId,
              exhibitorPairId,
            })}
            icon={TaskReviewIcon}
            name={t("common.tasks.title")}
            userRole={Roles.EXHIBITOR}
            calculateBadgeCount={() => Promise.resolve(badges.showTasksCount)}
          />
          <NavItem
            to={createLinkPath(SHOW_INFO_EXHIBITOR_PATH, {
              showId,
              exhibitorPairId,
            })}
            userRole={Roles.EXHIBITOR}
            icon={ShowInfoIcon}
            name={t("exhibitor.glossary.page_title", {show: localMap.show})}
          />
        </NavList>
      )}
      {isVendrIncluded && (
        <>
          {isTaskdIncluded && <Divider />}
          <NavList>
            <NavItem
              userRole={Roles.EXHIBITOR}
              to={shopPathes[0]}
              icon={ShoppingIcon}
              name={t("exhibitor.shop.title", {exhibitor: localMap.exhibitor})}
              selected={shopPathSelected}
            />
            <NavItem
              userRole={Roles.EXHIBITOR}
              to={createLinkPath(SHOW_EXHIBITOR_CART_PATH, {
                showId,
                exhibitorPairId,
              })}
              icon={ShoppingCartIcon}
              name={t("exhibitor.cart.title")}
              calculateBadgeCount={() => Promise.resolve(badges.showCartCount)}
            />
            <NavItem
              userRole={Roles.EXHIBITOR}
              to={createLinkPath(SHOW_EXHIBITOR_ORDERS_PATH, {
                showId,
                exhibitorPairId,
              })}
              icon={OrdersIcon}
              name={t("exhibitor.orders.page_title")}
            />
          </NavList>
        </>
      )}
      <Divider />
      <NavList>
        <NavItem
          userRole={Roles.EXHIBITOR}
          to={createLinkPath(SHOW_EXHIBITOR_USERS_PATH, {
            showId,
            exhibitorPairId,
          })}
          icon={HowToRegIcon}
          name={t("exhibitor.users.page_title")}
        />
      </NavList>
    </Box>
  );
};
const ExhibitorNavigation: FC = () => {
  const showId = useCurrentShowId();
  const {getBranding} = useBranding();
  const branding = getBranding(showId);
  const exhibitorShowTheme = useGenerateThemeFromBranding(branding);

  if (showId) {
    return (
      <ThemeProvider theme={exhibitorShowTheme}>
        <ShowNavigation />
      </ThemeProvider>
    );
  }
  return null;
};

export default ExhibitorNavigation;
